/**
 * Timeline Manager with Real Sync Functionality
 * 
 * Admin component for managing timeline aggregation with actual sync capabilities
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Card,
  CardHeader,
  CardBody,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  Badge,
  useToast,
  Spinner,
  Divider,
  Code,
  useColorModeValue,
  Progress,
  List,
  ListItem,
  ListIcon
} from '@chakra-ui/react';
import {
  Clock,
  Database,
  ArrowClockwise,
  ChartBar,
  CheckCircle,
  Warning,
  Info
} from '@phosphor-icons/react';
import { collection, getDocs, doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';

const TimelineManagerSync = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState('idle'); // idle, syncing, success, error
  const [timelineStats, setTimelineStats] = useState(null);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [syncProgress, setSyncProgress] = useState(0);
  
  const toast = useToast();

  // Color mode values
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // Load existing timeline data on component mount
  useEffect(() => {
    loadExistingTimelineData();
  }, []);

  // Load existing cached timeline data
  const loadExistingTimelineData = async () => {
    setIsLoading(true);
    try {
      const timelineRef = doc(db, 'system', 'aggregated_timelines');
      const timelineSnap = await getDoc(timelineRef);
      
      if (timelineSnap.exists()) {
        const data = timelineSnap.data();
        setTimelineStats({
          totalSites: data.totalSites || 0,
          sitesWithTimelines: data.sitesWithTimelines || 0,
          totalPeriods: data.allPeriods?.length || 0,
          coveragePercentage: Math.round(((data.sitesWithTimelines || 0) / (data.totalSites || 1)) * 100),
          lastUpdated: data.lastUpdated?.toDate?.() || new Date(data.generatedAt || Date.now())
        });
        setLastSyncTime(data.lastUpdated?.toDate?.() || new Date(data.generatedAt || Date.now()));
      } else {
        setTimelineStats({
          totalSites: 0,
          sitesWithTimelines: 0,
          totalPeriods: 0,
          coveragePercentage: 0
        });
      }
    } catch (error) {
      console.error('Error loading timeline data:', error);
      toast({
        title: 'Load Error',
        description: 'Failed to load existing timeline data.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Manual timeline sync function
  const handleTimelineSync = async () => {
    setIsSyncing(true);
    setSyncStatus('syncing');
    setSyncProgress(0);
    
    try {
      // Step 1: Fetch all sites
      setSyncProgress(10);
      const sitesRef = collection(db, 'sites');
      const sitesSnap = await getDocs(sitesRef);
      
      setSyncProgress(30);
      
      // Step 2: Process timeline data
      const timelineData = {
        totalSites: 0,
        sitesWithTimelines: 0,
        allPeriods: [],
        periodsByEra: {},
        periodsByCulture: {},
        periodsByRegion: {},
        timelineRange: {
          earliest: null,
          latest: null
        },
        lastUpdated: new Date(),
        generatedAt: new Date().toISOString()
      };

      setSyncProgress(50);

      // Process each site
      sitesSnap.docs.forEach((doc) => {
        const siteData = doc.data();
        const siteId = doc.id;
        
        timelineData.totalSites++;
        
        // Check if site has construction periods
        if (siteData.constructionPeriods && Array.isArray(siteData.constructionPeriods) && siteData.constructionPeriods.length > 0) {
          timelineData.sitesWithTimelines++;
          
          // Process each construction period
          siteData.constructionPeriods.forEach((period, index) => {
            const periodData = {
              siteId,
              siteName: siteData.name || 'Unknown Site',
              siteLocation: {
                region: siteData.location?.region || 'Unknown Region',
                country: siteData.location?.country || 'Unknown Country',
                coordinates: siteData.location?.coordinates || null
              },
              periodIndex: index,
              periodName: period.periodName || `Period ${index + 1}`,
              startEra: period.startEra || '',
              endEra: period.endEra || '',
              culture: period.culture || siteData.culture || 'Unknown Culture',
              features: period.features || [],
              details: period.details || '',
              isMainPeriod: index === (siteData.mainPeriodIndex || 0)
            };

            timelineData.allPeriods.push(periodData);

            // Group by era (simplified categorization)
            const era = categorizeByEra(periodData.startEra);
            if (!timelineData.periodsByEra[era]) {
              timelineData.periodsByEra[era] = [];
            }
            timelineData.periodsByEra[era].push(periodData);

            // Group by culture
            const culture = periodData.culture;
            if (!timelineData.periodsByCulture[culture]) {
              timelineData.periodsByCulture[culture] = [];
            }
            timelineData.periodsByCulture[culture].push(periodData);

            // Group by region
            const region = periodData.siteLocation.region;
            if (!timelineData.periodsByRegion[region]) {
              timelineData.periodsByRegion[region] = [];
            }
            timelineData.periodsByRegion[region].push(periodData);
          });
        }
      });

      setSyncProgress(80);

      // Step 3: Save aggregated data
      const timelineRef = doc(db, 'system', 'aggregated_timelines');
      await setDoc(timelineRef, timelineData);
      
      setSyncProgress(100);

      // Update local state
      setTimelineStats({
        totalSites: timelineData.totalSites,
        sitesWithTimelines: timelineData.sitesWithTimelines,
        totalPeriods: timelineData.allPeriods.length,
        coveragePercentage: Math.round((timelineData.sitesWithTimelines / timelineData.totalSites) * 100)
      });
      setLastSyncTime(new Date());
      setSyncStatus('success');
      
      toast({
        title: 'Sync Complete!',
        description: `Successfully synced ${timelineData.allPeriods.length} timeline periods from ${timelineData.sitesWithTimelines} sites.`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      
    } catch (error) {
      console.error('Error syncing timeline data:', error);
      setSyncStatus('error');
      toast({
        title: 'Sync Failed',
        description: error.message || 'Failed to sync timeline data.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSyncing(false);
      setTimeout(() => {
        setSyncStatus('idle');
        setSyncProgress(0);
      }, 3000);
    }
  };

  // Simple era categorization
  const categorizeByEra = (startEra) => {
    if (!startEra) return 'Unknown';
    
    const eraLower = startEra.toLowerCase();
    if (eraLower.includes('paleolithic')) return 'Paleolithic';
    if (eraLower.includes('mesolithic')) return 'Mesolithic';
    if (eraLower.includes('neolithic')) return 'Neolithic';
    if (eraLower.includes('bronze')) return 'Bronze Age';
    if (eraLower.includes('iron')) return 'Iron Age';
    if (eraLower.includes('medieval')) return 'Medieval';
    if (eraLower.includes('modern')) return 'Modern';
    
    // Try to parse BCE/CE dates for automatic categorization
    const match = startEra.match(/(\d+)\s*(BCE|CE)/i);
    if (match) {
      const year = parseInt(match[1]);
      const era = match[2].toUpperCase();
      const numericYear = era === 'BCE' ? -year : year;
      
      if (numericYear < -10000) return 'Paleolithic';
      if (numericYear < -8000) return 'Mesolithic';
      if (numericYear < -3000) return 'Neolithic';
      if (numericYear < -1200) return 'Bronze Age';
      if (numericYear < 500) return 'Iron Age';
      if (numericYear < 1500) return 'Medieval';
      return 'Modern';
    }
    
    return 'Unknown';
  };

  const getStatusColor = () => {
    switch (syncStatus) {
      case 'syncing': return 'blue';
      case 'success': return 'green';
      case 'error': return 'red';
      default: return 'gray';
    }
  };

  const getStatusIcon = () => {
    switch (syncStatus) {
      case 'syncing': return <Spinner size="sm" />;
      case 'success': return <CheckCircle size={16} />;
      case 'error': return <Warning size={16} />;
      default: return <Info size={16} />;
    }
  };

  return (
    <VStack spacing={6} align="stretch">
      {/* Header */}
      <Box>
        <HStack spacing={3} mb={2}>
          <Clock size={24} />
          <Heading size="lg">Timeline Sync Management</Heading>
        </HStack>
        <Text color="gray.500">
          Manually sync timeline data from all sites and view aggregation statistics.
        </Text>
      </Box>

      {/* Sync Status */}
      <Card>
        <CardHeader>
          <HStack justify="space-between">
            <Heading size="md">Sync Status</Heading>
            <Badge colorScheme={getStatusColor()}>
              {syncStatus === 'idle' ? 'Ready' : syncStatus}
            </Badge>
          </HStack>
        </CardHeader>
        <CardBody>
          <VStack spacing={4} align="stretch">
            <HStack>
              {getStatusIcon()}
              <Text>
                {syncStatus === 'idle' && 'Ready to sync timeline data'}
                {syncStatus === 'syncing' && 'Syncing timeline data...'}
                {syncStatus === 'success' && 'Timeline data synced successfully'}
                {syncStatus === 'error' && 'Sync failed - check console for details'}
              </Text>
            </HStack>

            {isSyncing && (
              <Box>
                <Text fontSize="sm" mb={2}>Progress: {syncProgress}%</Text>
                <Progress value={syncProgress} colorScheme="blue" />
              </Box>
            )}

            {lastSyncTime && (
              <Text fontSize="sm" color="gray.500">
                Last sync: {lastSyncTime.toLocaleString()}
              </Text>
            )}
          </VStack>
        </CardBody>
      </Card>

      {/* Sync Actions */}
      <Card>
        <CardHeader>
          <Heading size="md">Actions</Heading>
        </CardHeader>
        <CardBody>
          <VStack spacing={4} align="stretch">
            <HStack spacing={4}>
              <Button
                leftIcon={<Database size={16} />}
                colorScheme="blue"
                onClick={handleTimelineSync}
                isLoading={isSyncing}
                loadingText="Syncing..."
                size="lg"
              >
                Sync Timeline Data
              </Button>
              
              <Button
                leftIcon={<ArrowClockwise size={16} />}
                variant="outline"
                onClick={loadExistingTimelineData}
                isLoading={isLoading}
                loadingText="Loading..."
              >
                Refresh Stats
              </Button>
            </HStack>

            <Alert status="info" size="sm">
              <AlertIcon />
              <Box>
                <Text fontSize="sm">
                  <strong>Sync Timeline Data:</strong> Processes all sites in the database and aggregates their construction period information.
                </Text>
                <Text fontSize="sm" mt={1}>
                  <strong>Refresh Stats:</strong> Reloads the current timeline statistics without performing a full sync.
                </Text>
              </Box>
            </Alert>
          </VStack>
        </CardBody>
      </Card>

      {/* Current Statistics */}
      {timelineStats && (
        <Card>
          <CardHeader>
            <HStack justify="space-between">
              <Heading size="md">Current Statistics</Heading>
              <Badge colorScheme="purple">Live Data</Badge>
            </HStack>
          </CardHeader>
          <CardBody>
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
              <Stat>
                <StatLabel>Total Sites</StatLabel>
                <StatNumber>{timelineStats.totalSites}</StatNumber>
              </Stat>
              <Stat>
                <StatLabel>Sites with Timelines</StatLabel>
                <StatNumber>{timelineStats.sitesWithTimelines}</StatNumber>
                <StatHelpText>{timelineStats.coveragePercentage}% coverage</StatHelpText>
              </Stat>
              <Stat>
                <StatLabel>Construction Periods</StatLabel>
                <StatNumber>{timelineStats.totalPeriods}</StatNumber>
              </Stat>
              <Stat>
                <StatLabel>Data Freshness</StatLabel>
                <StatNumber fontSize="sm">
                  {lastSyncTime ? (
                    Math.round((Date.now() - lastSyncTime.getTime()) / (1000 * 60 * 60)) < 24 ? 'Fresh' : 'Stale'
                  ) : 'Unknown'}
                </StatNumber>
                <StatHelpText>
                  {lastSyncTime ? `${Math.round((Date.now() - lastSyncTime.getTime()) / (1000 * 60 * 60))}h ago` : 'No sync yet'}
                </StatHelpText>
              </Stat>
            </SimpleGrid>
          </CardBody>
        </Card>
      )}

      {/* Sync Process Info */}
      <Card>
        <CardHeader>
          <Heading size="md">How Timeline Sync Works</Heading>
        </CardHeader>
        <CardBody>
          <List spacing={3}>
            <ListItem>
              <ListIcon as={CheckCircle} color="green.500" />
              <Text as="span" fontSize="sm">
                <strong>Step 1:</strong> Fetches all site documents from the <Code>sites</Code> collection
              </Text>
            </ListItem>
            <ListItem>
              <ListIcon as={CheckCircle} color="green.500" />
              <Text as="span" fontSize="sm">
                <strong>Step 2:</strong> Extracts <Code>constructionPeriods</Code> data from each site
              </Text>
            </ListItem>
            <ListItem>
              <ListIcon as={CheckCircle} color="green.500" />
              <Text as="span" fontSize="sm">
                <strong>Step 3:</strong> Groups periods by era, culture, and region for analysis
              </Text>
            </ListItem>
            <ListItem>
              <ListIcon as={CheckCircle} color="green.500" />
              <Text as="span" fontSize="sm">
                <strong>Step 4:</strong> Saves aggregated data to <Code>system/aggregated_timelines</Code>
              </Text>
            </ListItem>
            <ListItem>
              <ListIcon as={CheckCircle} color="green.500" />
              <Text as="span" fontSize="sm">
                <strong>Step 5:</strong> Updates statistics and makes data available to the timeline viewer
              </Text>
            </ListItem>
          </List>
        </CardBody>
      </Card>
    </VStack>
  );
};

export default TimelineManagerSync;